import React from 'react';
import { createAITTheme } from '@this/shared/ui/theme';
import type Trip from '@this/domain/trip/trip2';
import type OrderItem from '@this/domain/order_item2';
import type { Element } from '@this/domain/order_item2';
import type TravelerInformation from '@this/src/domain/traveler_information';
import type TransportElement from '@this/domain/transport_element2';

// 共通の型定義
export interface ItemPriceProps {
  orderItem: OrderItem;
  changedPrice: number;
  ticketStateType: 'priceChanged' | 'priceNotChanged' | 'noFlight' | 'notApplicable';
}

export type OrderItemDescriptionProps = ItemPriceProps & {
  tripId: number;
  trip: Trip;
};

export interface OrderItemDescriptionWhenRequestFormProps {
  trip: Trip;
}

const FOREIGN_AIR_SEAT_TYPE_MAP = {
  M: 'エコノミー',
  C: 'ビジネス',
  F: 'ファースト',
  W: 'プレミアムエコノミー'
};

const isTransportElement = (el: Element): el is TransportElement => {
  return el.type === 'transport' && 'transportType' in el;
};

const isShinkansenTransport = (el: Element): boolean => {
  return isTransportElement(el) && el.transportType === 'shinkansen';
};

const isDomesticAirTransport = (el: Element): boolean => {
  return isTransportElement(el) && el.transportType === 'domestic_air';
};

const isForeignAirTransport = (el: Element): boolean => {
  return isTransportElement(el) && el.transportType === 'foreign_air';
};

const formatShinkansenSeatInfo = (tst: TravelerInformation, elementIndex: number | null = null): string => {
  const shinkansenParts: string[] = [];
  const ticketType = tst.getShinkansenTicketTypeText(elementIndex);
  const seatType = tst.getShinkansenSeatTypeText(elementIndex);

  if (ticketType) {
    shinkansenParts.push(`券種: ${ticketType}`);
  }
  if (seatType) {
    shinkansenParts.push(`座席: ${seatType}`);
  }
  if (tst.shinkansenSeat) {
    shinkansenParts.push(`座席希望: ${tst.shinkansenSeat}`);
  }
  return shinkansenParts.join(', ');
};

const formatDomesticAirSeatInfo = (el: Element, tst: TravelerInformation): string => {
  const domesticAirParts: string[] = [];
  if (isTransportElement(el)) {
    if (el.airSeatType) {
      domesticAirParts.push(`座席クラス: ${el.airSeatType}`);
    }
  }
  if (tst.airSeat) {
    domesticAirParts.push(`座席希望: ${tst.airSeat}`);
  } else {
    domesticAirParts.push(`座席希望: 指定なし`);
  }
  return domesticAirParts.join(', ');
};

const formatForeignAirSeatInfo = (el: Element, tst: TravelerInformation) => {
  const foreignAirParts = [];

  if (isTransportElement(el)) {
    if (el.cabin) {
      const seatClassText =
        FOREIGN_AIR_SEAT_TYPE_MAP[el.cabin as keyof typeof FOREIGN_AIR_SEAT_TYPE_MAP] || el.cabin;
      foreignAirParts.push(`座席クラス: ${seatClassText}`);
    }
  }

  if (tst.airSeat) {
    foreignAirParts.push(`座席希望: ${tst.airSeat}`);
  } else {
    foreignAirParts.push(`座席希望: 指定なし`);
  }

  return foreignAirParts.join(', ');
};

// 共通ユーティリティ関数
export const formatTravelerText = (
  el: Element,
  tst: TravelerInformation,
  travelerIndex: number,
  elementIndex: number | null = null
) => {
  let str = '';
  if (isShinkansenTransport(el)) {
    str = formatShinkansenSeatInfo(tst, elementIndex);
  } else if (isDomesticAirTransport(el)) {
    str = formatDomesticAirSeatInfo(el, tst);
  } else if (isForeignAirTransport(el)) {
    str = formatForeignAirSeatInfo(el, tst);
  }
  return `出張者:${travelerIndex + 1}人目　　(${tst.lastNameRoman} ${tst.firstNameRoman})　${str}`;
};

// メディアクエリ判定用のhook
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

// 共通コンポーネント
interface SectionDescTextProps {
  isOld: boolean;
  className?: string;
  children: React.ReactNode;
}

export const SectionDescText: React.FC<SectionDescTextProps> = ({ isOld, className, children }) => {
  const theme = createAITTheme();
  const style: React.CSSProperties = {
    color: className === 'red' ? theme.tokens.colors.danger.primary : undefined,
    textDecoration: isOld ? 'line-through' : undefined
  };

  return (
    <span className={className} style={style}>
      {children}
    </span>
  );
};

export const OutlineBox: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const theme = createAITTheme();

  const style: React.CSSProperties = {
    border: `1px solid ${theme.tokens.colors.border.divider}`,
    marginTop: '-1px',
    padding: isMobile ? '6px' : '20px',
    margin: '10px 0'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplication: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: isMobile ? 'column' : 'row'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplicationInfo: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: isMobile ? '100%' : '68%',
    padding: '10px'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplicationBox: React.FC = ({ children }) => {
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  };

  return <div style={style}>{children}</div>;
};

export const SectionLabel: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    padding: '10px',
    flexBasis: isMobile ? undefined : '16%',
    width: isMobile ? '100%' : undefined,
    fontWeight: 'bold'
  };

  return <div style={style}>{children}</div>;
};

export const SectionMarket: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    paddingTop: '10px',
    width: isMobile ? '100%' : '16%',
    textAlign: isMobile ? 'left' : 'right',
    paddingLeft: isMobile ? '10px' : undefined
  };

  return <div style={style}>{children}</div>;
};

export const SectionPrice: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    padding: '5px',
    fontWeight: 'bold',
    display: 'flex',
    width: isMobile ? '100%' : '90%',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  return <div style={style}>{children}</div>;
};

export const SectionBudgetHr: React.FC = () => {
  const theme = createAITTheme();
  const style: React.CSSProperties = {
    width: '100%',
    border: `1px solid ${theme.tokens.colors.border.divider}`,
    margin: '5px 0'
  };

  return <hr style={style} />;
};

export const SectionDesc: React.FC = ({ children }) => {
  const style: React.CSSProperties = {
    marginBottom: '10px'
  };

  return <div style={style}>{children}</div>;
};
